import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { Button } from '@/components/atoms/Button';
import { ChevronRight } from '@/components/atoms/Icons';
import { Link } from '@/components/atoms/Link';
import { Label } from '@/components/atoms/Typography/Label';
import CheckboxField from '@/components/molecules/FormFields/CheckboxField';
import { TextField } from '@/components/molecules/FormFields/TextField';
import { useAuth } from '@/context';
import { getBrowserApiBaseURL } from '@/lib/axiosConfig';

import styles from './LoginForm.module.css';
import { SSOLoginForm } from './SSOForm';
import WarningMessage from './WarningMessage';

import type { LoginFormData } from './LoginForm.types';

function LoginForm({ setMode, mode }: { setMode: (mode: 'password' | 'sso') => void; mode: 'sso' | 'password' }) {
  const router = useRouter();
  const { t } = useTranslation('login');
  const { login, loading } = useAuth();
  const passwordResetUrl = `${getBrowserApiBaseURL()}en-us/password_reset/`;

  const [loginStatus, setLoginStatus] = useState<number | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const form = useForm<LoginFormData>({
    defaultValues: {
      username: '',
      password: '',
      remember: false,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    clearErrors,
  } = form;

  const onSubmit = async (data: LoginFormData) => {
    clearErrors('root');
    setLoginStatus(null);

    const res = await login(data);

    if (res.logged) {
      await router.replace('/');
      return;
    }

    setLoginStatus(typeof res.status === 'number' ? res.status : null);
  };

  return (
    <>
      {mode === 'password' ? (
        <FormProvider {...form}>
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <TextField
              placeholder="example@email.com"
              autocomplete="username"
              name="username"
              label={t('form.username.label')}
              type="text"
              rules={{
                required: t('form.username.required'),
                onChange: () => {
                  if (loginStatus !== null) {
                    setLoginStatus(null);
                    clearErrors('root');
                  }
                },
              }}
            />

            <TextField
              autocomplete="current-password"
              name="password"
              label={t('form.password.label')}
              type={passwordVisible ? 'text' : 'password'}
              suffix={
                <button
                  type="button"
                  className={styles.passwordToggle}
                  aria-label={passwordVisible ? t('form.password.hide') : t('form.password.show')}
                  aria-pressed={passwordVisible}
                  onClick={() => setPasswordVisible((visible) => !visible)}
                >
                  {passwordVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </button>
              }
              rules={{
                required: t('form.password.required'),
                onChange: () => {
                  if (loginStatus !== null) {
                    setLoginStatus(null);
                    clearErrors('root');
                  }
                },
              }}
            />

            <CheckboxField name="remember" label={t('form.actions.remember')} />

            <Link href={passwordResetUrl} iconLeft={<ChevronRight className={styles.icon} />} target="_blank" size="sm">
              {t('form.actions.forgotPassword')}
            </Link>

            {loginStatus && (
              <div className={styles.warningMessage}>
                <WarningMessage status={loginStatus} setStatus={setLoginStatus} />
              </div>
            )}

            <Button aria-disabled={isSubmitting || !isValid} className="full" type="submit" isLoading={loading}>
              {isSubmitting ? t('form.actions.loading') : t('form.actions.login')}
            </Button>
          </form>
        </FormProvider>
      ) : (
        <SSOLoginForm setMode={setMode} />
      )}

      <div className={styles.ssoContainer}>
        <Label className={styles.ssoLabel} fontVariant="caps" size="lg" variant="secondary">
          {t('extraLoginOptionsTitle')}
        </Label>
        <Button variant="outlined" className="full" onClick={() => setMode('sso')}>
          {t('form.actions.ssoLogin')}
        </Button>
      </div>
    </>
  );
}

export default LoginForm;
